import { api } from './client'

export interface Activity {
  id: string
  dealId: string | null
  contactId: string | null
  authorUserId: string | null
  activityType: string
  body: string
  /** 'manual' = user-logged (editable) · 'system' = send audit (immutable). */
  source: string
  /** Pinned activities sort to the top of the timeline. */
  pinned: boolean
  /** Communication delivery status: 'sent' | 'received' | 'bounced' | 'failed' (null for non-comms). */
  status?: string | null
  /** The task this entry is about — present on task entries, so the timeline can open it. */
  taskId?: string | null
  /** What the recipient did with a sent email; null on every other entry and on sends with no activity. */
  engagement?: EmailEngagement | null
  /** Who a send went out as (the record's owner) — may differ from whoever clicked Send. */
  senderName?: string | null
  /** The number an SMS went out from, when the sender has their own. */
  fromNumber?: string | null
  createdAt: string
}

/** Opens and clicks on one sent email, rolled up for its timeline entry. */
export interface EmailEngagement {
  opens: number
  lastOpenedAt: string | null
  clicks: number
  lastClickedAt: string | null
}

export async function pinDealActivity(dealId: string, activityId: string, pinned: boolean): Promise<Activity> {
  return (await api.post<Activity>(`/api/deals/${dealId}/activities/${activityId}/pin`, { pinned })).data
}

export async function pinContactActivity(contactId: string, activityId: string, pinned: boolean): Promise<Activity> {
  return (await api.post<Activity>(`/api/contacts/${contactId}/activities/${activityId}/pin`, { pinned })).data
}

export async function listActivities(dealId: string): Promise<Activity[]> {
  const { data } = await api.get<Activity[]>(`/api/deals/${dealId}/activities`)
  return data
}

export async function addActivity(
  dealId: string,
  params: { activityType: string; body: string },
): Promise<Activity> {
  const { data } = await api.post<Activity>(`/api/deals/${dealId}/activities`, params)
  return data
}

export async function updateActivity(
  dealId: string,
  activityId: string,
  params: { activityType?: string; body: string },
): Promise<Activity> {
  const { data } = await api.put<Activity>(`/api/deals/${dealId}/activities/${activityId}`, params)
  return data
}

export async function deleteActivity(dealId: string, activityId: string): Promise<void> {
  await api.delete(`/api/deals/${dealId}/activities/${activityId}`)
}

export async function listContactActivities(contactId: string): Promise<Activity[]> {
  const { data } = await api.get<Activity[]>(`/api/contacts/${contactId}/activities`)
  return data
}

export async function addContactActivity(
  contactId: string,
  params: { activityType: string; body: string },
): Promise<Activity> {
  const { data } = await api.post<Activity>(`/api/contacts/${contactId}/activities`, params)
  return data
}

export async function updateContactActivity(
  contactId: string,
  activityId: string,
  params: { activityType?: string; body: string },
): Promise<Activity> {
  const { data } = await api.put<Activity>(
    `/api/contacts/${contactId}/activities/${activityId}`,
    params,
  )
  return data
}

export async function deleteContactActivity(contactId: string, activityId: string): Promise<void> {
  await api.delete(`/api/contacts/${contactId}/activities/${activityId}`)
}
